import { parseISO } from "date-fns";

import { UnavailableDate } from "@/data/api";
import { FBT, Locale } from "@/lib/i18n/i18n_provider";
import { Day } from "@/offering/day_utils";
import { formatMeetingRoomTimeRange } from "@/offering/format_date_range";
import { Time } from "@/offering/time_utils";
import {
  RecurrenceInterval,
  UnavailabilityReason,
} from "@/offering/recurrence";
import { RecurringExpansion } from "@/offering_v2/utils/expand_recurrence";

// Every customer-facing string of the repeat control lives here, so the naming
// rule can be checked in one place: the whole thing is "a recurring booking",
// one date is "a booking" or "a date", the control is "Repeat". The words
// "series", "occurrence", "instance" and "session" never reach the screen.
//
// Plurals ride inside the translated string (never inside a param), so the
// dictionary keys stay "{count} bookings" and friends — see src/locale/*.ts.

export function presetLabel(interval: RecurrenceInterval, fbt: FBT): string {
  switch (interval) {
    case 1:
      return fbt("Weekly");
    case 2:
      return fbt("Every 2 weeks");
    case 3:
      return fbt("Every 3 weeks");
    case 4:
      // Never "Monthly": across seven bookings this drifts about two weeks
      // earlier in the month, and a customer who reads "monthly" would expect
      // the same Tuesday of each month.
      return fbt("Every 4 weeks");
  }
}

// The endpoint's reason codes, in the customer's words. Two codes share a
// label where the distinction is the partner's, not the customer's.
export function reasonLabel(
  reason: UnavailabilityReason | undefined,
  fbt: FBT
): string {
  switch (reason) {
    case "closed":
    case "blockout":
      return fbt("location closed");
    case "booked":
      return fbt("already booked");
    case "lead_time":
    case "cutoff":
    case "elapsed":
      return fbt("too soon to book");
    case "min_duration":
      return fbt("too short for this room");
    default:
      return fbt("unavailable");
  }
}

function bookingsPlural(count: number, fbt: FBT): string {
  return fbt.plural("booking", count, {
    name: "count",
    showCount: "yes",
    many: "bookings",
  });
}

export function bookingsCount(count: number, fbt: FBT): string {
  return fbt(bookingsPlural(count, fbt));
}

export function shortDate(day: Day, fbt: FBT): string {
  return fbt.formatDate(parseISO(day), {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export function monthDayDate(day: Day, fbt: FBT): string {
  return fbt.formatDate(parseISO(day), { month: "short", day: "numeric" });
}

export function weekdayName(day: Day, fbt: FBT): string {
  return fbt.formatDate(parseISO(day), { weekday: "long" });
}

// "Every Monday" / "Every 2 weeks on Monday" — the rule as the confirmation
// states it. Never "monthly" (see presetLabel).
export function repeatRuleLabel(
  interval: RecurrenceInterval,
  anchor: Day,
  fbt: FBT
): string {
  const weekday = weekdayName(anchor, fbt);
  if (interval === 1) return fbt("Every " + fbt.param("weekday", weekday));
  return fbt(
    "Every " +
      fbt.param("interval", interval) +
      " weeks on " +
      fbt.param("weekday", weekday)
  );
}

// "9:00 AM → 1:15 PM" for an hourly rule; a daily one has no window.
export function recurringWindowLabel(
  date: Day,
  startTime: Time | undefined,
  endTime: Time | undefined,
  fbt: FBT,
  locale: Locale
): string | undefined {
  if (!startTime || !endTime) return undefined;
  return formatMeetingRoomTimeRange(
    { date, startTime, endTime },
    fbt,
    locale,
    ""
  );
}

// "Aug 31 – Nov 2, 2026" — the year once, on the end, when both share it.
export function dateSpanLabel(first: Day, last: Day, fbt: FBT): string {
  const firstDate = parseISO(first);
  const lastDate = parseISO(last);
  const withYear = { month: "short", day: "numeric", year: "numeric" } as const;
  const start =
    firstDate.getFullYear() === lastDate.getFullYear()
      ? monthDayDate(first, fbt)
      : fbt.formatDate(firstDate, withYear);
  return `${start} – ${fbt.formatDate(lastDate, withYear)}`;
}

// "Aug 31 – Nov 2, 2026 · 10 bookings"
export function bookingsInSpan(
  first: Day,
  last: Day,
  count: number,
  fbt: FBT
): string {
  return fbt(
    fbt.param("span", dateSpanLabel(first, last, fbt)) +
      " · " +
      bookingsPlural(count, fbt)
  );
}

// The confirmation's footnote for the pattern dates that were never booked.
// Says "not charged" outright: a customer counting rows against the total
// must not read a skipped date as a lost payment.
export function skippedDatesFootnote(
  skipped: Day[],
  fbt: FBT
): string | undefined {
  if (skipped.length === 0) return undefined;
  const list = skipped.map((day) => monthDayDate(day, fbt)).join(", ");
  if (skipped.length === 1) {
    return fbt(
      fbt.param("date", list) +
        " fell on your pattern but was already taken. It was left out of the recurring booking and was not charged."
    );
  }
  return fbt(
    fbt.param("dates", list) +
      " fell on your pattern but were already taken. They were left out of the recurring booking and were not charged."
  );
}

export function recurringConfirmationSentence(
  count: number,
  email: string,
  fbt: FBT
): string {
  return fbt(
    "Thank you for booking with us. A confirmation email covering all " +
      bookingsPlural(count, fbt) +
      " has been sent to " +
      fbt.param("email", email) +
      ". We are looking forward to seeing you soon."
  );
}

export function chargedOnceNote(fbt: FBT): string {
  return fbt("Charged once for the whole recurring booking.");
}

/**
 * "Sep 1, 2026, 9:00 AM" in the room's own zone — the refund deadline is a
 * moment, not a day, and a customer told only the date would read the whole of
 * it as still open. Undefined when there is no deadline to name.
 */
export function refundDeadlineLabel(
  iso: string | undefined,
  timeZone: string,
  fbt: FBT
): string | undefined {
  if (!iso) return undefined;
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return undefined;
  return fbt.formatDate(instant, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone,
  });
}

/**
 * The cancellation contract of a recurring booking, for the surfaces that state
 * a policy rather than offer a cancel button: the storefront's room page, the
 * checkout, and the confirmation.
 *
 * A recurring booking is refunded whole or not at all. It can be cancelled in
 * full until the first date's 100%-refund cutoff — after that a date can still
 * be released, which frees the room but returns no money. Both halves have to be
 * said: a customer who reads only the tier ladder of the first date expects the
 * same refund on date twelve.
 *
 * A deadline already behind us reads as no deadline at all. The ladder is
 * measured back from the first date, so a series bought inside its own
 * free-cancellation window is non-refundable from the moment of purchase, and
 * naming a cutoff in the past would promise a window that was never open to
 * this customer.
 */
export function recurringCancellationPolicy(
  refundableUntil: string | undefined,
  timeZone: string,
  fbt: FBT,
  now: Date = new Date()
): string {
  const deadline = new Date(refundableUntil ?? "");
  const stillOpen =
    !Number.isNaN(deadline.getTime()) && deadline.getTime() > now.getTime();
  const label = stillOpen
    ? refundDeadlineLabel(refundableUntil, timeZone, fbt)
    : undefined;

  if (!label) {
    return fbt(
      "Not refundable. A single date can be cancelled to free the room, but is not refunded."
    );
  }
  return fbt(
    "Refundable in full until " +
      fbt.param("deadline", label) +
      ". After that, a single date can be cancelled to free the room, but is not refunded."
  );
}

// What is still missing before a booking can repeat: hourly needs a time
// window, daily only a date. Shown by the switch while it is disabled and by
// the pane while Repeat is on without a window.
export function missingWindowHint(
  durationUnit: "hour" | "day",
  fbt: FBT
): string {
  return durationUnit === "hour"
    ? fbt("Choose a date and time to repeat this booking.")
    : fbt("Choose a date to repeat this booking.");
}

// Submit refused the recurring booking because these dates were taken in the
// meantime. Shown as a toast, so one sentence: which dates, and the way out.
export function lostDatesMessage(dates: UnavailableDate[], fbt: FBT): string {
  const list = dates.map((lost) => shortDate(lost.date, fbt)).join(", ");
  return fbt(
    "These dates are no longer available: " +
      fbt.param("dates", list) +
      ". Choose another time, or turn Repeat off."
  );
}

// The quote refused to repeat this booking. One sentence naming what happened
// and one naming the way out — the two actions beside it ("Update the dates",
// "Book a single date instead") are the same for every code, so the copy's job
// is to make the right one obvious.
export function recurringQuoteFailureMessage(
  code: string | undefined,
  serverMessage: string | undefined,
  fbt: FBT
): string {
  switch (code) {
    case "recurring_non_refundable_rate":
      return fbt(
        "This time is sold at a non-refundable rate, which cannot be part of a recurring booking. Try a different day or time, or book this date on its own."
      );
    case "recurring_non_refundable_promo":
      return fbt(
        "This promo code makes the booking non-refundable, which cannot be part of a recurring booking. Remove the code, or book this date on its own."
      );
    case "recurring_request_to_book":
      return fbt(
        "Bookings at this time need the venue's approval, so they cannot repeat yet. Try a different day or time, or book this date on its own."
      );
    case "recurring_date_in_past":
      return fbt(
        "The first date's start time has passed. Update the dates to keep this booking repeating."
      );
    case "recurring_off_horizon":
      return fbt(
        "The chosen dates no longer fit this room's booking window. Update the dates to keep this booking repeating."
      );
    case "recurring_too_few_dates":
      return fbt(
        "A recurring booking needs at least 2 dates. Add another date, or book this date on its own."
      );
    case "recurring_not_eligible":
      return fbt(
        "This room no longer offers recurring bookings. You can still book a single date."
      );
    default:
      // A code we don't know — an old backend that sends none, a new refusal,
      // or a failure that isn't about the recurrence at all (a sold-out date,
      // an invalid promo). The server's own sentence is the most truthful
      // thing on hand, so show it; the canned line only covers a failure that
      // arrived wordless.
      return (
        serverMessage ||
        fbt(
          "We couldn't price this recurring booking. Try different dates, or book a single date instead."
        )
      );
  }
}

export function updateDatesAction(fbt: FBT): string {
  return fbt("Update the dates");
}

export function bookSingleDateAction(fbt: FBT): string {
  return fbt("Book a single date instead");
}

export function removePromoCodeAction(fbt: FBT): string {
  return fbt("Remove the promo code");
}

export function repeatHint(anchor: Day | undefined, fbt: FBT): string {
  if (!anchor) return fbt("Same room and time, every week");
  return fbt(
    "Same room and time, every " +
      fbt.param("weekday", weekdayName(anchor, fbt))
  );
}

export function clampNote(
  expansion: RecurringExpansion,
  fbt: FBT
): string | undefined {
  if (!expansion.clamped || expansion.anchorUnavailable) return undefined;
  return fbt(
    "Shortened to " +
      bookingsPlural(expansion.booked.length, fbt) +
      " — a recurring booking has to finish inside the booking window, which ends " +
      fbt.param("date", monthDayDate(expansion.horizonEnd, fbt)) +
      "."
  );
}

// The picker footer's one-line summary of the draft.
export function summaryHeadline(expansion: RecurringExpansion, fbt: FBT) {
  const first = expansion.booked[0];
  const last = expansion.booked[expansion.booked.length - 1];
  if (!first || !last) return undefined;
  return fbt(
    bookingsPlural(expansion.booked.length, fbt) +
      " · " +
      fbt.param("first", monthDayDate(first, fbt)) +
      " – " +
      fbt.param("last", monthDayDate(last, fbt))
  );
}

export function summaryDetail(expansion: RecurringExpansion, fbt: FBT) {
  const skipped = expansion.skipped.length;
  if (!skipped) return fbt("Prices confirmed on Save");
  return fbt(
    fbt.plural("unavailable date", skipped, {
      name: "count",
      showCount: "yes",
      many: "unavailable dates",
    }) + " skipped · prices confirmed on Save"
  );
}

// The second line under the committed date in the picker's toggle.
export function committedRepeatLine(
  expansion: RecurringExpansion,
  fbt: FBT
): string | undefined {
  const last = expansion.booked[expansion.booked.length - 1];
  if (!last) return undefined;
  return fbt(
    "Repeats " +
      fbt.param(
        "preset",
        presetLabel(expansion.rule.interval, fbt).toLowerCase()
      ) +
      " · " +
      bookingsPlural(expansion.booked.length, fbt) +
      " through " +
      fbt.param("last", monthDayDate(last, fbt))
  );
}

// "Tue, Sep 1 through Tue, Nov 3" — the review screen's second line.
export function reviewSubline(expansion: RecurringExpansion, fbt: FBT): string {
  const first = expansion.booked[0];
  const last = expansion.booked[expansion.booked.length - 1];
  if (!first || !last) return "";
  return fbt(
    fbt.param("first", shortDate(first, fbt)) +
      " through " +
      fbt.param("last", shortDate(last, fbt))
  );
}

export function skippedNote(expansion: RecurringExpansion, fbt: FBT): string {
  const skipped = expansion.skipped;
  if (!skipped.length) return fbt("Every date on this pattern is available.");
  const list = skipped
    .map((s) => `${monthDayDate(s.date, fbt)} (${reasonLabel(s.reason, fbt)})`)
    .join(", ");
  return skipped.length === 1
    ? fbt(
        "1 date on this pattern is unavailable and not charged: " +
          fbt.param("list", list) +
          "."
      )
    : fbt(
        fbt.param("count", skipped.length) +
          " dates on this pattern are unavailable and not charged: " +
          fbt.param("list", list) +
          "."
      );
}
